import type { NomoCharacter } from '@/mocks/characters';
import type { CharacterDetail } from '@/mocks/characterDetail';

const toneMap: Record<string, { block: string; label: string }> = {
  primary: { block: 'bg-primary-50', label: 'text-primary-600' },
  accent: { block: 'bg-accent-50', label: 'text-accent-700' },
  secondary: { block: 'bg-secondary-50', label: 'text-secondary-700' },
};

interface HeroProps {
  character: NomoCharacter;
  detail?: CharacterDetail;
}

export default function Hero({ character, detail }: HeroProps) {
  const tone = toneMap[character.tone] ?? toneMap.primary;

  return (
    <section className="relative overflow-hidden bg-background-100">
      <div className="mx-auto max-w-6xl px-4 pb-14 pt-24 md:px-6 md:pb-20 md:pt-32">
        <nav className="mb-8 flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
          <a
            href="/"
            className="transition-colors duration-300 hover:text-primary-600 cursor-pointer"
          >
            首页
          </a>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <a
            href="/#characters"
            className="transition-colors duration-300 hover:text-primary-600 cursor-pointer"
          >
            IP 角色
          </a>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">{character.name}</span>
        </nav>

        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="reveal order-2 lg:order-1">
            <p className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
              {character.placeEn}
            </p>
            <h1 className="mt-4 font-heading text-4xl leading-tight text-foreground-950 md:text-6xl">
              {character.name}
              <span className="ml-4 align-middle text-sm tracking-[0.24em] text-foreground-400 uppercase md:text-base">
                {character.enName}
              </span>
            </h1>
            <p className="mt-5 font-heading text-xl text-foreground-800 md:text-2xl">
              {character.tagline}
            </p>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-foreground-600 md:text-base">
              {detail?.intro ?? character.origin}
            </p>

            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className={`rounded-md px-4 py-3 ${tone.block}`}>
                <p className={`text-[11px] tracking-[0.2em] uppercase ${tone.label}`}>Flavor</p>
                <p className="mt-1 text-sm text-foreground-800">{character.flavor}</p>
              </div>
              <div className="rounded-md bg-secondary-50 px-4 py-3">
                <p className="text-[11px] tracking-[0.2em] text-secondary-700 uppercase">Place</p>
                <p className="mt-1 text-sm text-foreground-800">{character.place}</p>
              </div>
              <div className="rounded-md bg-accent-50 px-4 py-3">
                <p className="text-[11px] tracking-[0.2em] text-accent-700 uppercase">Wish</p>
                <p className="mt-1 text-sm text-foreground-800">{character.wish}</p>
              </div>
            </div>
          </div>

          <div className="reveal order-1 lg:order-2" style={{ transitionDelay: '120ms' }}>
            <div className="relative mx-auto aspect-square w-full max-w-[440px] overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
              <img
                src={character.image}
                alt={`${character.name} NOMOISM 东方潮玩角色`}
                title={`NOMOISM ${character.name} · ${character.flavor}`}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-4 top-4 rounded-full bg-primary-500 px-3.5 py-1.5 text-[11px] tracking-[0.16em] text-background-50">
                {character.debut ? '首个 NOMO 灵' : '待登场 NOMO 灵'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}